import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";
import { LanguageToggle } from "../components/LanguageToggle";
import { Footer } from "../components/Footer";
import { useI18n } from "../lib/i18n";

const EMAIL = "contact@localcreators.fr";

const DOCS = {
  mentions: {
    titleKey: "linkLegalNotice",
    content: {
      fr: [
        ["Éditeur du site", "Le site Local Creators est édité par Local Creators SAS, société par actions simplifiée au capital de 10 000 €, dont le siège social est situé à Lyon (France), immatriculée au RCS de Lyon. Directeur de la publication : le représentant légal de la société."],
        ["Hébergement", "Le site est hébergé par un prestataire d'hébergement cloud, dont les coordonnées sont disponibles sur simple demande à " + EMAIL + "."],
        ["Propriété intellectuelle", "L'ensemble des contenus (marque, logo, textes, visuels, code) sont la propriété exclusive de Local Creators SAS, sauf mention contraire."],
        ["Contact", "Pour toute question, écrivez-nous à " + EMAIL + "."],
      ],
      en: [
        ["Publisher", "The Local Creators website is published by Local Creators SAS, a simplified joint-stock company with share capital of €10,000, headquartered in Lyon (France), registered with the Lyon Trade Register. Publication director: the company's legal representative."],
        ["Hosting", "The site is hosted by a cloud hosting provider whose details are available on request at " + EMAIL + "."],
        ["Intellectual property", "All content (brand, logo, texts, visuals, code) is the exclusive property of Local Creators SAS unless otherwise stated."],
        ["Contact", "For any question, write to us at " + EMAIL + "."],
      ],
      vi: [
        ["Đơn vị phát hành", "Website Local Creators do Local Creators SAS phát hành, công ty cổ phần đơn giản vốn 10.000 €, trụ sở tại Lyon (Pháp), đăng ký tại Sổ Thương mại Lyon. Giám đốc xuất bản: người đại diện pháp lý của công ty."],
        ["Lưu trữ", "Website được lưu trữ bởi nhà cung cấp đám mây; thông tin có sẵn khi yêu cầu tại " + EMAIL + "."],
        ["Sở hữu trí tuệ", "Toàn bộ nội dung (thương hiệu, logo, văn bản, hình ảnh, mã) thuộc sở hữu độc quyền của Local Creators SAS trừ khi có ghi chú khác."],
        ["Liên hệ", "Mọi câu hỏi, hãy viết cho chúng tôi tại " + EMAIL + "."],
      ],
    },
  },
  cgv: {
    titleKey: "linkTerms",
    content: {
      fr: [
        ["Objet", "Les présentes CGV régissent l'abonnement des créateurs à la plateforme ainsi que les paiements des collaborations entre commerces et créateurs."],
        ["Abonnement créateur", "L'accès aux missions par les créateurs est conditionné à un abonnement mensuel dont le montant est indiqué à la souscription. Reconductible tacitement, résiliable à tout moment ; la résiliation prend effet en fin de période."],
        ["Paiement des collaborations", "Les paiements sont traités de manière sécurisée via Stripe. Local Creators peut prélever une commission de service."],
        ["Remboursement", "Toute demande peut être adressée à " + EMAIL + " et sera traitée conformément à la réglementation."],
      ],
      en: [
        ["Purpose", "These terms govern creator subscriptions to the platform and payments for collaborations between businesses and creators."],
        ["Creator subscription", "Access to missions requires a monthly subscription whose amount is shown at sign-up. Automatically renewed, cancellable at any time; cancellation takes effect at the end of the period."],
        ["Collaboration payments", "Payments are securely processed via Stripe. Local Creators may charge a service commission."],
        ["Refunds", "Requests can be sent to " + EMAIL + " and will be handled in accordance with applicable regulations."],
      ],
      vi: [
        ["Mục đích", "Các điều khoản này điều chỉnh gói đăng ký của nhà sáng tạo và các khoản thanh toán hợp tác giữa doanh nghiệp và nhà sáng tạo."],
        ["Gói đăng ký", "Việc truy cập nhiệm vụ yêu cầu gói hàng tháng, số tiền hiển thị khi đăng ký. Tự động gia hạn, có thể hủy bất kỳ lúc nào; hiệu lực hủy vào cuối kỳ."],
        ["Thanh toán hợp tác", "Thanh toán được xử lý an toàn qua Stripe. Local Creators có thể thu hoa hồng dịch vụ."],
        ["Hoàn tiền", "Yêu cầu gửi tới " + EMAIL + " và được xử lý theo quy định hiện hành."],
      ],
    },
  },
  cgu: {
    titleKey: "linkTermsUse",
    content: {
      fr: [
        ["Acceptation", "En utilisant Local Creators, l'utilisateur accepte les présentes conditions d'utilisation."],
        ["Compte", "L'utilisateur est responsable de la confidentialité de ses identifiants et de l'activité de son compte."],
        ["Comportement", "Les contenus doivent être authentiques et licites. Tout contenu illicite ou trompeur pourra entraîner la suspension du compte."],
        ["Responsabilité", "Local Creators agit comme intermédiaire de mise en relation et n'est pas responsable de l'exécution des prestations."],
      ],
      en: [
        ["Acceptance", "By using Local Creators, the user accepts these terms of use."],
        ["Account", "The user is responsible for the confidentiality of their credentials and account activity."],
        ["Conduct", "Content must be authentic and lawful. Any unlawful or misleading content may lead to account suspension."],
        ["Liability", "Local Creators acts as a matchmaking intermediary and is not responsible for the performance of services."],
      ],
      vi: [
        ["Chấp thuận", "Khi sử dụng Local Creators, người dùng chấp nhận các điều khoản sử dụng này."],
        ["Tài khoản", "Người dùng chịu trách nhiệm bảo mật thông tin đăng nhập và hoạt động tài khoản."],
        ["Hành vi", "Nội dung phải chân thực và hợp pháp. Nội dung bất hợp pháp hoặc gây hiểu lầm có thể dẫn đến khóa tài khoản."],
        ["Trách nhiệm", "Local Creators đóng vai trò trung gian kết nối và không chịu trách nhiệm về việc thực hiện dịch vụ."],
      ],
    },
  },
  confidentialite: {
    titleKey: "linkPrivacy",
    content: {
      fr: [
        ["Données collectées", "Identité, email, rôle, localisation approximative, contenus livrés et données de paiement (traitées par Stripe)."],
        ["Finalités", "Mise en relation, gestion des abonnements et paiements, sécurité et amélioration du service."],
        ["Vos droits (RGPD)", "Droit d'accès, de rectification, d'effacement, de portabilité et d'opposition. Contactez " + EMAIL + "."],
        ["Conservation", "Les données sont conservées pour la durée nécessaire puis supprimées ou anonymisées."],
      ],
      en: [
        ["Data collected", "Identity, email, role, approximate location, delivered content and payment data (processed by Stripe)."],
        ["Purposes", "Matchmaking, subscription and payment management, security and service improvement."],
        ["Your rights (GDPR)", "Right of access, rectification, erasure, portability and objection. Contact " + EMAIL + "."],
        ["Retention", "Data is kept for the necessary duration then deleted or anonymized."],
      ],
      vi: [
        ["Dữ liệu thu thập", "Danh tính, email, vai trò, vị trí gần đúng, nội dung bàn giao và dữ liệu thanh toán (xử lý bởi Stripe)."],
        ["Mục đích", "Kết nối, quản lý gói và thanh toán, bảo mật và cải thiện dịch vụ."],
        ["Quyền của bạn (GDPR)", "Quyền truy cập, chỉnh sửa, xóa, di chuyển và phản đối. Liên hệ " + EMAIL + "."],
        ["Lưu giữ", "Dữ liệu được giữ trong thời gian cần thiết rồi xóa hoặc ẩn danh."],
      ],
    },
  },
  cookies: {
    titleKey: "linkCookies",
    content: {
      fr: [
        ["Utilisation des cookies", "Cookies strictement nécessaires (authentification, langue) et, avec consentement, cookies de mesure d'audience."],
        ["Gestion", "Vous pouvez configurer votre navigateur pour refuser les cookies non essentiels. La langue est stockée localement."],
      ],
      en: [
        ["Cookie use", "Strictly necessary cookies (authentication, language) and, with consent, audience measurement cookies."],
        ["Management", "You can configure your browser to refuse non-essential cookies. Language is stored locally."],
      ],
      vi: [
        ["Sử dụng cookie", "Cookie thiết yếu (đăng nhập, ngôn ngữ) và, khi đồng ý, cookie đo lường khán giả."],
        ["Quản lý", "Bạn có thể cấu hình trình duyệt để từ chối cookie không thiết yếu. Ngôn ngữ được lưu cục bộ."],
      ],
    },
  },
  accessibilite: {
    titleKey: "linkAccessibility",
    content: {
      fr: [
        ["Notre engagement", "Local Creators vise l'accessibilité au plus grand nombre (RGAA / WCAG 2.1 AA)."],
        ["Mesures", "Contrastes suffisants, navigation clavier, textes alternatifs, tailles lisibles et structure sémantique."],
        ["Signalement", "Signalez toute difficulté à " + EMAIL + "."],
      ],
      en: [
        ["Our commitment", "Local Creators aims for broad accessibility (WCAG 2.1 AA)."],
        ["Measures", "Sufficient contrast, keyboard navigation, alt text, legible sizes and semantic structure."],
        ["Reporting", "Report any difficulty to " + EMAIL + "."],
      ],
      vi: [
        ["Cam kết", "Local Creators hướng tới khả năng tiếp cận rộng rãi (WCAG 2.1 AA)."],
        ["Biện pháp", "Độ tương phản đủ, điều hướng bàn phím, văn bản thay thế, cỡ chữ dễ đọc và cấu trúc ngữ nghĩa."],
        ["Báo cáo", "Báo mọi khó khăn tới " + EMAIL + "."],
      ],
    },
  },
  contact: {
    titleKey: "linkContact",
    content: {
      fr: [
        ["Nous écrire", "Une question, un partenariat, une demande presse ? Écrivez-nous à " + EMAIL + "."],
        ["Adresse", "Local Creators SAS — Lyon, France."],
      ],
      en: [
        ["Write to us", "A question, a partnership, a press request? Write to us at " + EMAIL + "."],
        ["Address", "Local Creators SAS — Lyon, France."],
      ],
      vi: [
        ["Viết cho chúng tôi", "Câu hỏi, hợp tác, yêu cầu báo chí? Viết cho chúng tôi tại " + EMAIL + "."],
        ["Địa chỉ", "Local Creators SAS — Lyon, Pháp."],
      ],
    },
  },
};

export default function Legal() {
  const { doc } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const entry = DOCS[doc] || DOCS.mentions;
  const sections = entry.content[lang] || entry.content.fr;

  useEffect(() => { window.scrollTo(0, 0); }, [doc]);

  return (
    <div className="min-h-screen bg-ink text-white">
      <div className="pointer-events-none fixed inset-0 grain opacity-40" />
      <header className="sticky top-0 z-40 glass border-b border-cardline">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
          <button onClick={() => navigate("/")} data-testid="legal-logo-btn"><Logo size={28} /></button>
          <LanguageToggle />
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 py-10" data-testid={`legal-${doc}`}>
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 transition-colors hover:text-gold" data-testid="legal-back-link">
          <ArrowLeft size={16} /> {t("backHome")}
        </Link>
        <h1 className="font-heading text-3xl font-extrabold sm:text-4xl">{t(entry.titleKey)}</h1>
        <p className="mt-2 text-xs text-slate-500">{t("lastUpdate")} : 01/06/2026</p>

        <div className="mt-8 space-y-8">
          {sections.map(([title, body], i) => (
            <section key={i}>
              <h2 className="font-heading text-lg font-semibold text-gold">{title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-300">{body}</p>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
